import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.tsx';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { toast } from 'sonner';
import type {
  AccountType,
  CreateCustomerOnboardingRequest,
  CreateCustomerOnboardingResponse,
  RealEstateAccountType,
} from '@metron/shared';
import { createLogger } from '@metron/shared';

const logger = createLogger('CreateCustomerOnboardingModal');

interface CreateCustomerOnboardingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateEntry: (
    data: CreateCustomerOnboardingRequest,
  ) => Promise<CreateCustomerOnboardingResponse>;
}

export function CreateCustomerOnboardingModal({
  open,
  onOpenChange,
  onCreateEntry,
}: CreateCustomerOnboardingModalProps) {
  const [formData, setFormData] = useState({
    waterscopeAccountId: '',
    metronId: '',
    accountType: '' as AccountType | '',
    realEstateAccountType: '' as RealEstateAccountType | '',
    salesforceId: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [createdEntry, setCreatedEntry] = useState<CreateCustomerOnboardingResponse | null>(null);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.waterscopeAccountId.trim()) {
      newErrors.waterscopeAccountId = 'WaterScope Account ID is required';
    }

    if (!formData.metronId.trim()) {
      newErrors.metronId = 'Metron ID is required';
    } else if (!/^[0-9]{10}$/.test(formData.metronId)) {
      newErrors.metronId = 'Metron ID must be exactly 10 digits';
    }

    if (!formData.accountType) {
      newErrors.accountType = 'Account Type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsLoading(true);
    try {
      const data: CreateCustomerOnboardingRequest = {
        waterscopeAccountId: formData.waterscopeAccountId,
        metronId: formData.metronId,
        accountType: formData.accountType as AccountType,
        realEstateAccountType: formData.realEstateAccountType || undefined,
        salesforceId: formData.salesforceId || undefined,
      };

      const result = await onCreateEntry(data);
      setCreatedEntry(result);
      toast.success('Customer onboarding entry created successfully');
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to create onboarding entry';
      toast.error(errorMessage);
      logger.error({ error }, 'Failed to create onboarding entry');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      waterscopeAccountId: '',
      metronId: '',
      accountType: '',
      realEstateAccountType: '',
      salesforceId: '',
    });
    setErrors({});
    setCreatedEntry(null);
    onOpenChange(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        {!createdEntry
          ? (
            <>
              <DialogHeader>
                <DialogTitle>Create Customer Onboarding Entry</DialogTitle>
                <DialogDescription>
                  Create a new customer onboarding queue entry. All fields marked with * are
                  required.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className='grid gap-4 py-4'>
                  <div className='grid gap-2'>
                    <Label htmlFor='waterscopeAccountId'>
                      WaterScope Account ID <span className='text-destructive'>*</span>
                    </Label>
                    <Input
                      id='waterscopeAccountId'
                      value={formData.waterscopeAccountId}
                      onChange={(e) => handleInputChange('waterscopeAccountId', e.target.value)}
                      placeholder='Enter WaterScope Account ID'
                      className={errors.waterscopeAccountId ? 'border-destructive' : ''}
                      disabled={isLoading}
                      required
                    />
                    {errors.waterscopeAccountId && (
                      <p className='text-sm text-destructive'>{errors.waterscopeAccountId}</p>
                    )}
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='metronId'>
                      Metron ID <span className='text-destructive'>*</span>
                    </Label>
                    <Input
                      id='metronId'
                      value={formData.metronId}
                      onChange={(e) => handleInputChange('metronId', e.target.value)}
                      placeholder='Enter 10-digit Metron ID'
                      maxLength={10}
                      className={errors.metronId ? 'border-destructive' : ''}
                      disabled={isLoading}
                      required
                    />
                    {errors.metronId && (
                      <p className='text-sm text-destructive'>{errors.metronId}</p>
                    )}
                    <p className='text-sm text-muted-foreground'>Must be exactly 10 digits</p>
                  </div>

                  <div className='grid gap-2'>
                    <Label htmlFor='accountType'>
                      Account Type <span className='text-destructive'>*</span>
                    </Label>
                    <Select
                      value={formData.accountType}
                      onValueChange={(value) => handleInputChange('accountType', value)}
                      disabled={isLoading}
                    >
                      <SelectTrigger
                        id='accountType'
                        className={errors.accountType ? 'border-destructive' : ''}
                      >
                        <SelectValue placeholder='Select account type' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='Utility'>Utility</SelectItem>
                        <SelectItem value='Real Estate'>Real Estate</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.accountType && (
                      <p className='text-sm text-destructive'>{errors.accountType}</p>
                    )}
                  </div>

                  {formData.accountType === 'Real Estate' && (
                    <div className='grid gap-2'>
                      <Label htmlFor='realEstateAccountType'>Real Estate Account Type</Label>
                      <Select
                        value={formData.realEstateAccountType}
                        onValueChange={(value) => handleInputChange('realEstateAccountType', value)}
                        disabled={isLoading}
                      >
                        <SelectTrigger id='realEstateAccountType'>
                          <SelectValue placeholder='Select real estate type (optional)' />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='CMFI'>CMFI</SelectItem>
                          <SelectItem value='MHP'>MHP</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className='text-sm text-muted-foreground'>
                        Optional for Real Estate accounts
                      </p>
                    </div>
                  )}

                  <div className='grid gap-2'>
                    <Label htmlFor='salesforceId'>Salesforce ID</Label>
                    <Input
                      id='salesforceId'
                      value={formData.salesforceId}
                      onChange={(e) => handleInputChange('salesforceId', e.target.value)}
                      placeholder='Enter Salesforce ID (optional)'
                      disabled={isLoading}
                    />
                    <p className='text-sm text-muted-foreground'>Optional</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button type='submit' disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create Entry'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )
          : (
            <>
              <DialogHeader>
                <DialogTitle>Entry Created Successfully</DialogTitle>
                <DialogDescription>
                  The customer onboarding entry has been added to the queue.
                </DialogDescription>
              </DialogHeader>
              <div className='grid gap-4 py-4'>
                <div className='grid gap-2'>
                  <Label>Entry ID</Label>
                  <div className='text-sm font-mono bg-muted p-2 rounded'>
                    {createdEntry.entry.id}
                  </div>
                </div>
                <div className='grid gap-2'>
                  <Label>WaterScope Account ID</Label>
                  <div className='text-sm bg-muted p-2 rounded'>
                    {createdEntry.entry.waterscopeAccountId}
                  </div>
                </div>
                <div className='grid gap-2'>
                  <Label>Metron ID</Label>
                  <div className='text-sm font-mono bg-muted p-2 rounded'>
                    {createdEntry.entry.metronId}
                  </div>
                </div>
                <div className='grid gap-2'>
                  <Label>Account Type</Label>
                  <div className='text-sm bg-muted p-2 rounded'>
                    {createdEntry.entry.accountType}
                    {createdEntry.entry.realEstateAccountType &&
                      ` - ${createdEntry.entry.realEstateAccountType}`}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleClose}>Done</Button>
              </DialogFooter>
            </>
          )}
      </DialogContent>
    </Dialog>
  );
}
